/**
 * Centralized ranking utilities
 *
 * IMPORTANT: This application uses 0-based ranking throughout.
 * - rank: 0 = highest priority
 * - rank: 1 = second priority
 * - etc.
 *
 * This matches backend Pydantic models and standard array indexing.
 */

export interface Rankable {
  rank: number;
}

/**
 * Sort items by rank (ascending: 0 first, higher numbers last)
 */
export function sortByRank<T extends Rankable>(items: T[]): T[] {
  return [...items].sort((a, b) => a.rank - b.rank);
}

/**
 * Assign ranks to items based on their array position
 * Returns new array with updated ranks (0-based)
 */
export function assignRanks<T extends Rankable>(items: T[]): T[] {
  return items.map((item, index) => ({
    ...item,
    rank: index,  // 0-based ranking
  }));
}

/**
 * Get initial rank for a new item (goes to top)
 */
export function getInitialRank(): number {
  return 0;  // 0 = highest priority
}

/**
 * Reorder items after drag-and-drop
 * Returns new array with updated ranks
 */
export function reorderItems<T extends Rankable>(
  items: T[],
  sourceIndex: number,
  destinationIndex: number
): T[] {
  const sorted = sortByRank(items);
  const newItems = [...sorted];
  const [reorderedItem] = newItems.splice(sourceIndex, 1);
  newItems.splice(destinationIndex, 0, reorderedItem);

  return assignRanks(newItems);
}

/**
 * Convert rank to display number (1-based for UI)
 * Use this ONLY for display purposes
 */
export function rankToDisplayNumber(rank: number): number {
  return rank + 1;
}

/**
 * Convert display number to rank (0-based for storage)
 * Use this if users input rank numbers
 */
export function displayNumberToRank(displayNumber: number): number {
  return Math.max(0, displayNumber - 1);
}

/**
 * Extract rank updates from items for API payload
 */
export function extractRankUpdates<T extends Rankable & { id: string }>(
  items: T[]
): Array<{ id: string; rank: number }> {
  return items.map(item => ({
    id: item.id,
    rank: item.rank,
  }));
}
